import { bopApi } from '@/lib/api/client';
import { formatFriendlyTimestamp } from '@/utils/format';

const COMMENT_PATH = 'v1/user/comment';

/**
 * Raw bop-table audit record. Every row for the pid except the
 * `login_info_` rows (those are the Auth tab's login history). Heterogeneous:
 * system rows (e.g. `status_change`) may lack `requester`/`comment`/`t`.
 */
export interface RawAuditRecord {
  /** pid */
  p: string;
  /** sort key `{kind}_{epochMs}` — use as React key */
  s: string;
  /** epoch ms — display sort key. Declared optional: some system rows lack it. */
  t?: number;
  /** operator name */
  requester?: string;
  /** the justification text */
  comment?: string;
  /** action description, e.g. 'Jane Doe blocked user <pid>' — ABSENT for plain comments */
  note?: string;
  /** action-specific fields ride along (e.g. status_change carries `action`) */
  [extra: string]: unknown;
}

export interface AuditEntry extends RawAuditRecord {
  /** friendly-formatted `t` ('' when `t` is absent) */
  time: string;
  /** the NOTE STRING when present, the literal 'comment' otherwise */
  type: string;
}

/**
 * Reshape moved client-side from the Nuxt BFF `audit/[PID].get.ts`:
 * friendly time, `type = note || 'comment'`, sorted by raw `t` DESCENDING.
 */
export const toAuditEntries = (records: RawAuditRecord[]): AuditEntry[] =>
  records
    .map((record) => ({
      ...record,
      // FIX: records missing `t` rendered 'NaN' in legacy — show blank instead
      time: typeof record.t === 'number' ? formatFriendlyTimestamp(record.t) : '',
      type: record.note || 'comment',
    }))
    .sort((a, b) => (b.t ?? 0) - (a.t ?? 0));

export const getUserAudit = async (pid: string): Promise<AuditEntry[]> => toAuditEntries(await bopApi.get(`v1/user/audit/${pid}`).json<RawAuditRecord[]>());

export interface CommentUserRequest {
  /** 36 chars — 400 'Invalid pid' */
  pid: string;
  /**
   * SECURITY: REQUIRED — bop-api does NOT read the auth token for
   * this endpoint. The legacy Nuxt BFF injected the session user's name; callers must
   * inject `useAuthStore`'s operator name and never let it be empty.
   */
  requester: string;
  /** free-form — deliberately NO 35–500 justification bounds */
  comment: string;
}

/**
 * KNOWN: responds 200 with an empty body even when the dynamo write
 * failed (the lambda swallows errors) — callers optimistically append + invalidate.
 * Empty body: never `.json()` this call.
 */
export const postUserComment = (body: CommentUserRequest): Promise<string> => bopApi.post(COMMENT_PATH, { json: body }).text();

/** Raw login-history row. `created`/`expiration` are EPOCH SECONDS. */
export interface LoginHistoryItem {
  /** pid */
  p: string;
  /** sort key `login_info_…` — React key */
  s: string;
  session_token?: string;
  hash_key?: string;
  id_token?: string;
  client_ip?: string;
  /** ISO-2 code */
  client_country?: string;
  /** EPOCH SECONDS — never mix with the audit table's ms `t` through custom math */
  created: number;
  expiration?: number;
  device_id?: string;
  /** 'google-oauth2', 'apple', … — absent for password logins */
  external_idp?: string;
  /** AML report — pre-rendered text lines; presence marks the row clickable */
  R?: string[];
  [extra: string]: unknown;
}

/**
 * Already sorted newest-first by dynamo (ScanIndexForward:false); the client-side
 * re-sort is kept as a cheap guarantee, matching the legacy Nuxt BFF.
 */
export const getLoginHistory = async (pid: string): Promise<LoginHistoryItem[]> => {
  const items = await bopApi.get(`v1/auth/user/${pid}/login-history`).json<LoginHistoryItem[]>();
  return [...items].sort((a, b) => b.created - a.created);
};
